import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import Label from '../label/label';
import { TaskInventory as TaskInventoryType, CustomUnitsDefinition, Scan } from '@bringg/types';
import { get as _get, isNil as _isNil, capitalize as _capitalize } from 'lodash';
import { TASK_INVENTORY_CONSTANTS } from './task-inventory-form/task-inventory-form';
import classNames from 'classnames';
import { UseFormMethods } from 'react-hook-form';
import TooltipWrapper from '../tooltip/tooltip';
import { DateDisplay } from '../index';
import { getDateFormat } from '../date-display/date-display';
import { useTimezone } from '../../features/waypoint-card/hooks/use-timezone';
import { getTaskInventoryTotals, TaskInventoryTotals } from 'components/task-inventories/utils/task-inventories-utils';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

export interface Translations {
	collectText: string;
	collectedText: string;
	deliverText: string;
	deliveredText: string;
	rejectedText: string;
	editItem: string;
	quantity: string;
	trashItem: string;
	width: string;
	height: string;
	inventoryLength: string;
	save: string;
	cancel: string;
	dimensions: string;
	scanCode: string;
	weight: string;
	price: string;
	note: string;
	itemArrival: string;
	searchPlaceholder: string;
	collapseAll: string;
	expandAll: string;
	items: string;
	total_weight: string;
	tooltip_explanation_weight: string;
	tooltip_inventory_rejected: string;
	orderId: string;
}

interface TaskInventoryProps {
	parentTaskInventory: TaskInventoryType;
	depth?: number;
	translations: Translations;
	isInEditMode?: boolean;
	form?: UseFormMethods;
	editable?: boolean;
	shownFields?: Object;
	getAllTaskInventoryChildren?: (inventory: TaskInventoryType) => TaskInventoryType[];
	getTaskInventoryTotals?: (
		inventory: TaskInventoryType,
		customUnitsDefinition: CustomUnitsDefinition,
		doNotMultipleWeightByQuantity?: boolean
	) => TaskInventoryTotals;
	customUnitsDefinition: CustomUnitsDefinition;
	taskInventoryScanMap?: Map<number, Scan>;
	doNotMultipleWeightByQuantity?: boolean;
	shouldShowLinkSourceTask?: boolean;
}

enum TaskInventoryFields {
	ScanString = 'scan_string',
	Dimensions = 'dimensions',
	Weight = 'weight',
	HandlingUnits = 'handling_units',
	Price = 'price',
	Note = 'note',
	ScheduledArrival = 'scheduled_arrival',
	Width = 'width',
	Height = 'height',
	Length = 'length'
}

const EMPTY_FIELD_TEXT = 'N/A';
const MAX_CHARACTERS: number = 20;
const tryLimitLengthString = (stringToLimit: string) => {
	if (stringToLimit?.length > MAX_CHARACTERS) {
		return `${stringToLimit.slice(0, 20)}...`;
	}
	return stringToLimit;
};

const getNumericFieldAsString = (object: any, path: string): string => {
	const value = _get(object, path);
	if (_isNil(value)) {
		return EMPTY_FIELD_TEXT;
	}
	const formattedValue = Math.round(value * 100) / 100;
	return formattedValue.toString();
};

const getTextualField = (object: any, path: string): string => {
	return _get(object, path) || EMPTY_FIELD_TEXT;
};

const getTaskInventoryDimensions = (taskInventory: TaskInventoryType): string => {
	// LxWxH is an industry standard
	const length = getNumericFieldAsString(taskInventory, TaskInventoryFields.Length);
	const width = getNumericFieldAsString(taskInventory, TaskInventoryFields.Width);
	const height = getNumericFieldAsString(taskInventory, TaskInventoryFields.Height);

	if (_isNil(width) || _isNil(height) || _isNil(length)) {
		return;
	}

	return `${length} x ${width} x ${height}`;
};
const VerticalLine = () => <span className="vertical-line"> </span>;
const TOOLTIP_COLOR = '#333333';
const SCHEDULED_ARRIVAL_FORMAT: string = 'MMM D hA';

const TaskInventory: React.FC<TaskInventoryProps> = ({
	parentTaskInventory,
	isInEditMode = false,
	form,
	translations,
	editable = true,
	depth = 0,
	shownFields,
	getAllTaskInventoryChildren,
	customUnitsDefinition,
	taskInventoryScanMap,
	doNotMultipleWeightByQuantity,
	shouldShowLinkSourceTask
}) => {
	const [rootTaskInventory, setRootTaskInventory] = useState(parentTaskInventory);
	const editableInEditMode = isInEditMode && editable;
	const timezone = useTimezone();
	const scanIcon = taskInventoryScanMap?.get(rootTaskInventory.id)?.manual_scan
		? BringgFontIcons.HandPointingUp
		: BringgFontIcons.Scanner;

	useEffect(() => {
		setRootTaskInventory(parentTaskInventory);
	}, [parentTaskInventory]);

	const statusToLabelProps = pending => {
		const { quantity, original_quantity, rejected_quantity } = rootTaskInventory;
		const hasNotDeliveredChildren: boolean = hasChildrenNotDelivered();
		const shouldShowRejectIndication: boolean = !hasNotDeliveredChildren && !!rejected_quantity;
		const itemLabel = `${pending ? translations.collectText : translations.deliverText} ${
			quantity || 0
		} / ${original_quantity}`;

		const content = (
			<div className="label-container" title={itemLabel}>
				<Label
					text={itemLabel}
					size="small"
					className={classNames({
						orange: shouldShowRejectIndication
					})}
				></Label>
				{hasNotDeliveredChildren && <div className="circle"></div>}
			</div>
		);

		return rootTaskInventory?.rejected_quantity > 0 ? (
			<TooltipWrapper
				color={TOOLTIP_COLOR}
				data-test-id="reject-tooltip"
				title={getRejectedInventoriesTooltipText(rootTaskInventory)}
			>
				{content}
			</TooltipWrapper>
		) : (
			content
		);
	};

	const getRejectedInventoriesTooltipText = (inventory: TaskInventoryType) => {
		const rejectDetails = inventory?.inventory_reject_details;
		const latestReasonText = rejectDetails?.[rejectDetails.length - 1]?.reason;
		return inventory.rejected_quantity + ' x ' + (latestReasonText || translations.tooltip_inventory_rejected);
	};

	const hasChildrenNotDelivered = (): boolean => {
		const taskInventoryChildren = getAllTaskInventoryChildren(rootTaskInventory);
		const isNotDelivered = taskInventoryChildren.filter(item => {
			return item.rejected_quantity > 0;
		});

		return !!isNotDelivered.length;
	};

	const getHandlingUnitsItems = () => {
		const handlingUnits =
			rootTaskInventory[TaskInventoryFields.HandlingUnits] ||
			(rootTaskInventory?.inventory && rootTaskInventory.inventory[TaskInventoryFields.HandlingUnits]);
		const handlingUnitsItems = [];
		for (const key in customUnitsDefinition) {
			const item = {
				content: getNumericFieldAsString(handlingUnits, key),
				className: `task-inventory-item task-inventory-${key}`,
				icon: BringgFontIcons.Pallets,
				tooltipContent: _capitalize(customUnitsDefinition[key].name),
				path: key
			};
			handlingUnitsItems.push(item);
		}
		return handlingUnitsItems;
	};

	const TASK_INVENTORY_ITEMS = useMemo(
		() => [
			{
				content: getTextualField(rootTaskInventory, TaskInventoryFields.ScanString),
				className: 'task-inventory-item task-inventory-scan-string text-ellipsis',
				icon: BringgFontIcons.Barcode,
				tooltipContent: translations.scanCode,
				path: TaskInventoryFields.ScanString
			},
			{
				content: getTaskInventoryDimensions(rootTaskInventory),
				className: 'task-inventory-item task-inventory-dimensions',
				icon: BringgFontIcons.Ruler,
				tooltipContent: `${translations.inventoryLength} x ${translations.width} x ${translations.height}`,
				path: TaskInventoryFields.Dimensions
			},
			{
				content: getNumericFieldAsString(rootTaskInventory, TaskInventoryFields.Weight),
				className: 'task-inventory-item task-inventory-weight',
				icon: BringgFontIcons.Weight,
				tooltipContent: translations.weight,
				path: TaskInventoryFields.Weight
			},
			...getHandlingUnitsItems(),
			{
				content: getNumericFieldAsString(rootTaskInventory, TaskInventoryFields.Price),
				className: 'task-inventory-item task-inventory-price',
				icon: BringgFontIcons.Coins,
				tooltipContent: translations.price,
				path: TaskInventoryFields.Price
			},
			{
				content: tryLimitLengthString(getTextualField(rootTaskInventory, TaskInventoryFields.Note)),
				className: 'task-inventory-item task-inventory-notes',
				icon: BringgFontIcons.NoteLight,
				tooltipContent: `${translations.note}: ${getTextualField(rootTaskInventory, TaskInventoryFields.Note)}`,
				path: TaskInventoryFields.Note
			},
			{
				content: rootTaskInventory.scheduled_arrival ? (
					<DateDisplay
						date={rootTaskInventory.scheduled_arrival}
						format={SCHEDULED_ARRIVAL_FORMAT}
						timezone={timezone}
					/>
				) : (
					EMPTY_FIELD_TEXT
				),
				className: 'task-inventory-item task-inventory-scheduled-arrival',
				icon: BringgFontIcons.ClockLight,
				tooltipContent: (
					<>
						{`${translations.itemArrival} `}
						<DateDisplay
							date={rootTaskInventory.scheduled_arrival}
							format={getDateFormat()}
							timezone={timezone}
						/>
					</>
				),
				path: TaskInventoryFields.ScheduledArrival
			}
		],
		[rootTaskInventory, translations]
	);

	const getFormItemName = useCallback(
		(propertyName: string): string => {
			return rootTaskInventory.id + TASK_INVENTORY_CONSTANTS.nameDelimiter + propertyName;
		},
		[rootTaskInventory]
	);

	const taskInventoryData = TASK_INVENTORY_ITEMS.map((item, index) => {
		if (_isNil(item.content) || !shownFields[item?.path]) return;

		return (
			<React.Fragment key={parentTaskInventory.id.toString() + index}>
				<TooltipWrapper color={TOOLTIP_COLOR} title={item.tooltipContent}>
					<div className={item.className}>
						{item.path === TaskInventoryFields.ScanString && rootTaskInventory?.scanned && (
							<BringgIcon iconName={scanIcon} className="scanner-icon" />
						)}
						<BringgIcon iconName={item.icon} className="task-inventory-icon" />
						<span className="task-inventory-content-text" title={item.content}>
							{item.content}
						</span>
					</div>
				</TooltipWrapper>
				{index < TASK_INVENTORY_ITEMS.length - 1 && <VerticalLine />}
			</React.Fragment>
		);
	});
	const onTaskInventoryDelete = useCallback(() => {
		form.setValue(getFormItemName(TASK_INVENTORY_CONSTANTS.originalQuantity), 0, { shouldDirty: true });
	}, [form]);

	const getRootTaskInventoryName = () => _get(rootTaskInventory, 'name', rootTaskInventory.external_id);

	const getInventoryTitle = (): string | ReactElement => {
		const originalQuantity = rootTaskInventory.original_quantity;
		const title = getRootTaskInventoryName();

		if (
			shouldShowLinkSourceTask &&
			!depth &&
			!rootTaskInventory.parent_task_inventory_id &&
			rootTaskInventory.source_task_id
		) {
			return (
				<>
					{originalQuantity} x {translations.orderId}:{' '}
					<a
						data-test-id="link-to-source-task-id"
						href={`/#/history/${rootTaskInventory.source_task_id}`}
						target="_blank"
						rel="noreferrer"
						onClick={e => {
							e.stopPropagation();
						}}
					>
						{title}
					</a>
				</>
			);
		}

		return `${originalQuantity} x ${title}`;
	};

	const getInventoryParenthesys = (): ReactElement => {
		let arrayOfTitles: { key: string; text: string; tootlip: string | undefined }[] = [];
		if (rootTaskInventory.inventories?.length) {
			const { items, weight, handling_units } = getTaskInventoryTotals(
				rootTaskInventory,
				customUnitsDefinition,
				doNotMultipleWeightByQuantity
			);

			arrayOfTitles.push({
				key: 'items',
				text: `${translations.items}: ${items}`,
				tootlip: undefined
			});
			if (weight) {
				arrayOfTitles.push({
					key: 'weight',
					text: `${translations.total_weight}: ${Math.round(weight * 100) / 100}`,
					tootlip: translations.tooltip_explanation_weight
				});
			}
			for (const key in handling_units) {
				if (handling_units[key].sum) {
					arrayOfTitles.push({
						key: `handling-units ${key}`,
						text: `${_capitalize(handling_units[key].name)}: ${handling_units[key].sum}`,
						tootlip: undefined
					});
				}
			}
		}

		if (arrayOfTitles.length) {
			return (
				<div className="parenthesys">
					(
					{arrayOfTitles.map((item, index) => {
						return (
							<div className={`item-totals ${item.key}`} key={item.key}>
								{index !== 0 ? <div className="separator-bar"> | </div> : <></>}
								<div className="text">
									{item.tootlip ? (
										<TooltipWrapper color={TOOLTIP_COLOR} title={item.tootlip}>
											{item.text}
										</TooltipWrapper>
									) : (
										item.text
									)}
								</div>
							</div>
						);
					})}
					)
				</div>
			);
		}
	};

	const getTaskInventoryTitle = () => (
		<>
			{getInventoryTitle()} {getInventoryParenthesys()}
		</>
	);

	const getRootTaskInventoryExternalId = () => _get(rootTaskInventory, 'external_id');

	return (
		<div
			className="task-inventory text-ellipsis"
			data-test-id={rootTaskInventory.id.toString()}
			style={{ marginLeft: `${depth * 3}%` }}
		>
			{depth > 0 && <div className="line" />}
			<div className="task-inventory-header">
				<div className="task-inventory-title text-ellipsis" title={getRootTaskInventoryName()}>
					{getTaskInventoryTitle()}
				</div>
			</div>
			<div className="task-inventory-content">
				<VerticalLine />
				<div className="task-inventory-quantity task-inventory-item">
					<span>{statusToLabelProps(rootTaskInventory.pending)}</span>
				</div>
				<VerticalLine />
				<div
					className="task-inventory-id task-inventory-item text-ellipsis"
					title={getRootTaskInventoryExternalId()}
				>
					<TooltipWrapper color={TOOLTIP_COLOR} title="#ID">
						#{getRootTaskInventoryExternalId()}
					</TooltipWrapper>

					{editableInEditMode && (
						<span onClick={onTaskInventoryDelete}>
							<BringgIcon iconName={BringgFontIcons.Trash} className="task-inventory-delete" />
						</span>
					)}
				</div>
				<VerticalLine />
				{taskInventoryData}
			</div>
		</div>
	);
};
export default TaskInventory;
